import { Grid, Link, Typography, useTheme } from '@mui/material';
import { useProductsTFunction } from '@retail/products/i18n';
import Tooltip from '@mui/material/Tooltip';
import { CopyToClipboard } from '@retail/components';
import { QuestionIcon } from '@shared/custom-icons';
import { Chip } from '@shared/components';
import { MgItemDetailsProductGroup } from './MgItemDetailsProductGroup';
import { MgItemCard } from '@retail/products/types';
import { MgItemDetailsPackaging } from './MgItemDetailsPackaging';

export interface Props {
  mgItem: MgItemCard;
  isSuperUser: boolean;
}

export const firstColumnSize = { xs: 6.8, lg: 4.8 };
export const secondColumnSize = { xs: 5.2, lg: 7.2 };

export function MgItemView({ mgItem, isSuperUser }: Props) {
  const { mgItemNumber, replacedByMgItemNumber } = mgItem;

  const t = useProductsTFunction();
  const theme = useTheme();
  const { tint } = theme.vars.palette.semantic;

  const firstSupplierItem = mgItem.getFirstSupplierItem();
  const itemGroupLabel = firstSupplierItem?.productGroup?.getLabel();

  return (
    <Grid container spacing={2} width="100%">
      <Grid fontWeight={600} size={firstColumnSize}>
        {t('products.mgItem.number.label')}
      </Grid>
      <Grid fontWeight="medium" display="flex" alignItems="center" size={secondColumnSize}>
        <CopyToClipboard value={mgItemNumber}>
          <Chip backgroundColor={tint.sky.soft} textColor={tint.sky.text} label={mgItemNumber} />
        </CopyToClipboard>

        <Tooltip arrow title={t('products.mgItem.number.description')}>
          <QuestionIcon sx={{ color: 'text.secondary' }} fontSize="small" />
        </Tooltip>
      </Grid>
      <MgItemDetailsProductGroup label={itemGroupLabel} />

      <Grid fontWeight={600} size={firstColumnSize}>
        {t(`products.mgItem.details.articleType`)}
      </Grid>
      <Grid fontWeight="medium" size={secondColumnSize}>
        <Typography color="text.secondary">
          {firstSupplierItem?.articleType ?? 'STANDARD'}
        </Typography>
      </Grid>
      <Grid fontWeight={600} size={firstColumnSize}>
        {t('products.mgItem.supplierItem.secondaryText')}
      </Grid>
      <Grid fontWeight="medium" color="text.secondary" size={secondColumnSize}>
        {firstSupplierItem?.secondaryText}
      </Grid>
      {replacedByMgItemNumber && (
        <Grid
          fontWeight={600}
          sx={{ display: 'flex', alignItems: 'center' }}
          size={firstColumnSize}
        >
          {t('products.mgItem.details.replacedByItem')}
        </Grid>
      )}
      {replacedByMgItemNumber && (
        <Grid fontWeight="medium" color="text.secondary" size={secondColumnSize}>
          <CopyToClipboard value={replacedByMgItemNumber}>
            <Link href={`/items/${replacedByMgItemNumber}`}>
              <Typography variant="body2">{replacedByMgItemNumber}</Typography>
            </Link>
          </CopyToClipboard>
        </Grid>
      )}
      <Grid sx={{ display: 'flex', overflow: 'hidden', width: '100%', mt: 3 }}>
        <MgItemDetailsPackaging uniquePackages={mgItem.getPackages()} isSuperUser={isSuperUser} />
      </Grid>
    </Grid>
  );
}
